import React from 'react'
import { Folder, MoreVertical, Trash2 } from 'lucide-react'
import { cn } from '../lib/utils'
import { Button } from '@iws/admin-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@iws/admin-ui/dropdown-menu'
import { MediaFolder } from '../types'

interface MediaFolderItemProps {
  folder: MediaFolder
  onNavigate: (id: string) => void
  onDelete?: (id: string, name: string) => void
  canDelete?: boolean
}

export const MediaFolderItem = ({
  folder,
  onNavigate,
  onDelete,
  canDelete = false,
}: MediaFolderItemProps) => {
  return (
    <div className="relative group">
      <div
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-lg border border-border-subtle bg-card p-3 pr-10 outline-none transition-colors',
          'hover:bg-subdued focus-visible:ring-2 focus-visible:ring-ring',
        )}
        role="button"
        tabIndex={0}
        onClick={() => onNavigate(folder._id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onNavigate(folder._id)
          }
        }}
      >
        <div className="grid size-8 shrink-0 place-items-center rounded-md bg-fill">
          <Folder className="size-4 text-text-2" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{folder.name}</p>
          {folder.item_count !== undefined && (
            <p className="text-xs tabular-nums text-text-2">{folder.item_count} items</p>
          )}
        </div>
      </div>

      {canDelete && onDelete && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Folder actions"
              className="absolute right-2 top-1/2 z-10 -translate-y-1/2 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
            >
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-error focus:bg-error-lighter focus:text-error-dark"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(folder._id, folder.name)
              }}
            >
              <Trash2 className="mr-2 size-4" /> Delete folder
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
